import {
    DesignVersion,
    DesignVersionId,
    DesignVersionsState
} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Dialog} from '@mintdata-wsp/wsp/ui/components/forms/Dialog';
import {DialogButton} from '@mintdata-wsp/wsp/ui/components/forms/DialogButton';
import {ErrorPopupSide, Input, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {bind} from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./NameCurrentVersionDialog.less');
const cx = classnames.bind(styles);

interface INameCurrentVersionDialogProps
{
    versionId?:DesignVersionId;
}

@observer
export class NameCurrentVersionDialog extends React.Component<INameCurrentVersionDialogProps, any>
{
    @observable
    private _name:string = null;

    @observable
    private _description:string = null;

    @observable
    private _isCreating:boolean = false;

    public constructor(props:INameCurrentVersionDialogProps)
    {
        super(props);

        if( props.versionId )
        {
            const state:DesignVersionsState = controllers.ui.designVersions.getState();
            const versionToRename = state.versions.find(
                (version:DesignVersion) => version.id === props.versionId
            );

            this._name = versionToRename.name;
            this._description = versionToRename.description;
        }
        else
        {
            this._name = controllers.ui.designVersions.getNextVersionName();
            this._description = '';
        }
    }

    @action.bound
    private async _onSaveVersionName():Promise<void>
    {
        if( !this._isValid(this._name) || !this._isValid(this._description) ) return;

        this._isCreating = true;

        const {versionId} = this.props;

        if( versionId )
            await controllers.ui.designVersions.renameVersion(versionId, this._name, this._description);
        else
            await controllers.ui.designVersions.nameCurrentVersion(this._name, this._description);

        controllers.ui.dialogs.hideDialog(Dialogs.NameCurrentVersion);

        runInAction(() => this._isCreating = false);
    }

    @bind
    private _onClose():void
    {
        controllers.ui.dialogs.hideDialog(Dialogs.NameCurrentVersion);
    }

    @action.bound
    private _onChangedName(newValue:string):void
    {
        this._name = newValue;
    }

    @action.bound
    private _onChangedDescription(newValue:string):void
    {
        this._description = newValue;
    }

    public render():JSX.Element
    {
        const isSaveBtnDisabled = this._name.trim() === '' || this._description.trim() === '' || this._isCreating;

        return (
            <Dialog
                title={'Name current version'}
                onClose={this._onClose}
            >
                <Column className={cx('content')}>

                    <div className={styles.message}>
                        Give the current design a friendly version name to easily come back to it later
                    </div>

                    <Input
                        className={cx('input')}
                        placeholder={'Version name'}
                        initialValue={this._name}
                        isInitiallyFocused={true}
                        style={InputStyle.Dialog}
                        errorPopupSide={ErrorPopupSide.Bottom}
                        isValidFn={this._isValid}
                        getErrorMessageFn={(value:string) =>
                        {
                            return `Please enter a name for your version`;
                        }}

                        onChange={this._onChangedName}
                        onEnterKey={this._onSaveVersionName}
                    />

                    <Input
                        className={cx('input')}
                        placeholder={'Description'}
                        initialValue={this._description}
                        style={InputStyle.Dialog}
                        errorPopupSide={ErrorPopupSide.Bottom}
                        isValidFn={this._isValid}
                        getErrorMessageFn={(value:string) =>
                        {
                            return `Please enter a description for your version`;
                        }}

                        onChange={this._onChangedDescription}
                        onEnterKey={this._onSaveVersionName}
                    />

                    <DialogButton
                        caption={'Save'}
                        onClick={this._onSaveVersionName}
                        isDisabled={isSaveBtnDisabled || !this._isValid(this._name) || !this._isValid(this._description)}
                    />

                </Column>
            </Dialog>
        );
    }

    private _isValid(value:string):boolean
    {
        return value && value.length > 0 && value.length < 255 && value.trim() !== '';
    }
}
